import { Extensions } from '@tiptap/react';
import Blockquote from '@tiptap/extension-blockquote';
import BulletList from '@tiptap/extension-bullet-list';
import CodeBlock from '@tiptap/extension-code-block';
import { Heading } from './HeadingExtension';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import Italic from '@tiptap/extension-italic';
import Bold from '@tiptap/extension-bold';
import Strike from '@tiptap/extension-strike';
import TableRow from '@tiptap/extension-table-row';
import { Color } from './ColorExtension';
import Code from '@tiptap/extension-code';
import History from '@tiptap/extension-history';
import { Link } from './LinkExtension';
import { PlaceholderExtension } from './PlaceholderExtension';
import { PresentationExtension } from './PresentationExtension';
import { InternalLinkExtension, LinkItem } from './InternalLinkExtension';
import { ImagePickerExtension } from './ImagePickerExtension';
import { TableHeader, TableCell, Table } from './TableExtension';
import { ArdoqId } from '@ardoq/api-types';
import {
  defaultTableNodeViewLabels,
  TableNodeViewLabels,
} from './TableNodeView';
import { PresentationDialogLabels } from './PresentationSelectorDialog';

export type ExtensionLabels = {
  table?: TableNodeViewLabels;
  tableMenuButton?: string;
  presentation?: PresentationDialogLabels;
};

export type RichTextEditorExtension =
  | 'Heading'
  | 'CondensedHeading'
  | 'Bold'
  | 'Italic'
  | 'Strike'
  | 'Color'
  | 'Blockquote'
  | 'HorizontalRule'
  | 'BulletList'
  | 'OrderedList'
  | 'Table'
  | 'Code'
  | 'CodeBlock'
  | 'History'
  | {
      name: 'Link';
      config?: {
        /**
         * If enabled, it allows presentation links to be added to the editor. Should not be enabled
         * when the presentation extension is enabled.
         * @default true
         */
        includePresentationLinks?: boolean;
      };
    }
  | {
      name: 'Placeholders';
      items: { value: string; label: string }[];
    }
  | {
      name: 'Presentation';
      items: { url: string; label: string }[];
    }
  | {
      name: 'InternalLink';
      linkPicker: (workspaceId?: string) => Promise<LinkItem[]>;
    }
  | {
      name: 'ImagePicker';
      imagePicker: (workspaceId?: string) => Promise<{ uri: string }[]>;
    };

export const parseRichTextEditorExtension = (
  extension: RichTextEditorExtension,
  workspaceId?: ArdoqId,
  extensionLabels?: ExtensionLabels
): Extensions => {
  switch (extension) {
    case 'Heading':
      return [Heading];
    case 'CondensedHeading':
      return [Heading.configure({ levels: [1, 3, 5] })];
    case 'Bold':
      return [Bold];
    case 'Italic':
      return [Italic];
    case 'Strike':
      return [Strike];
    case 'Color':
      return [Color];
    case 'Blockquote':
      return [Blockquote];
    case 'HorizontalRule':
      return [HorizontalRule];
    case 'BulletList':
      return [ListItem, BulletList];
    case 'OrderedList':
      return [ListItem, OrderedList];
    case 'Table':
      return [
        Table.configure({
          resizable: false,
          labels: extensionLabels?.table ?? defaultTableNodeViewLabels,
          menuButtonLabel: extensionLabels?.tableMenuButton ?? 'Menu',
        }),
        TableCell,
        TableHeader,
        TableRow,
      ];
    case 'Code':
      return [Code];
    case 'CodeBlock':
      return [CodeBlock];
    case 'History':
      return [History];
    default:
  }
  switch (extension?.name) {
    case 'Link':
      return [Link.configure(extension?.config)];
    case 'Placeholders':
      return [PlaceholderExtension.configure(extension)];
    case 'Presentation':
      return [
        PresentationExtension.configure({
          ...extension,
          labels: extensionLabels?.presentation,
        }),
      ];
    case 'InternalLink':
      return [
        InternalLinkExtension.configure({
          linkPicker: () => extension.linkPicker(workspaceId),
        }),
      ];
    case 'ImagePicker':
      return [
        ImagePickerExtension.configure({
          imagePicker: () => extension.imagePicker(workspaceId),
        }),
      ];
    default:
      return [];
  }
};

/**
 * Registered in every editor so existing images survive a parse/serialise
 * round-trip. An editor given no picker offers no way to insert an image, so
 * the input rule inherited from `@tiptap/extension-image` is dropped with it.
 */
export const imageNodeWithoutPicker = ImagePickerExtension.configure({
  imagePicker: null,
}).extend({
  addInputRules: () => [],
});

export const defaultExtensions: RichTextEditorExtension[] = [
  'Heading',
  'Bold',
  'Italic',
  'Strike',
  'Color',
  'Blockquote',
  'HorizontalRule',
  'BulletList',
  'OrderedList',
  'Table',
  {
    name: 'Link',
  },
  'Code',
  'CodeBlock',
];
